// Financial statements by fiscal year (Stage 2F, migration 018): trial balance, profit & loss, balance sheet and general
// ledger of one fiscal year. Income and expense restart each year; a result not yet closed is shown as a derived equity
// line, never posted by the report. A year with open items stays PROVISIONAL and the open items are listed — an
// undetermined figure is shown as OPEN, never as zero.
import { useState, useEffect, useMemo } from "react";
import { Notice, Select, ExportMenu, StatusBadge, SkeletonTableRows } from "./kit/components";
import { T, text } from "./kit/tokens";
import { fyStatus, fyTrialBalance, profitLoss, balanceSheet, fyLedger } from "./api";
import { errText, TYPE_INFO, PROVISIONAL_ACTIVATION_NOTE } from "./shared";
import { npr, DateChip } from "./workflow";
import PeriodStatements from "./PeriodStatements";

const cell = { padding: "9px 12px", borderBottom: `1px solid ${T.borderSoft}`, fontSize: 13, verticalAlign: "top" };
const num = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const head = { ...cell, fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.6, background: T.surface2 };
const mono = { fontFamily: "ui-monospace, Consolas, monospace", color: T.textMuted, marginRight: 8 };
const REPORTS = [["tb", "Trial balance"], ["pl", "Profit & loss"], ["bs", "Balance sheet"], ["gl", "General ledger"],
    ["pl_period", "Profit & loss — any period"], ["bs_asof", "Balance sheet — as at a date"], ["cf_period", "Cash flow — any period"]];   // S1 + S2 (migration 022)
const FLEX = { pl_period: "pl", bs_asof: "bs", cf_period: "cf" };
const TOTAL = /^(total_|net_result)/;

function Table({ cols, children, minWidth = 560 }) {
    return (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
                <thead><tr>{cols.map(([label, right], i) => <th key={i} style={{ ...head, textAlign: right ? "right" : "left" }}>{label}</th>)}</tr></thead>
                <tbody>{children}</tbody>
            </table>
        </div>
    );
}

export default function FinancialReports({ ctx }) {
    const { data, cal, display, openEntry, version } = ctx;
    const years = useMemo(() => [...data.years].sort((a, b) => b.start_date.localeCompare(a.start_date)), [data.years]);
    const [fyId, setFyId] = useState(() => {
        const today = new Date().toISOString().slice(0, 10);
        return (years.find(y => y.start_date <= today && today <= y.end_date) || years[0])?.id || "";
    });
    const [report, setReport] = useState("tb");
    const postable = data.accounts.filter(a => a.is_postable).sort((a, b) => a.code.localeCompare(b.code));
    const [accountId, setAccountId] = useState(() => (data.accounts.find(a => a.code === "1121") || postable[0])?.id || "");
    const [status, setStatus] = useState(null);
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    const fy = years.find(y => y.id === fyId);
    const account = data.accounts.find(a => a.id === accountId);
    const openItems = (data.hist?.openItems || []).filter(o => o.fiscal_year_id === fyId);

    useEffect(() => {
        if (!fyId) return;
        let live = true;   // ignore a reply that arrives after the user picked another year
        setStatus(null);
        fyStatus(fyId).then(s => live && setStatus(s)).catch(e => live && setErr(errText(e)));
        return () => { live = false; };
    }, [fyId, version]);
    useEffect(() => {
        if (!fyId || FLEX[report] || (report === "gl" && !accountId)) return;
        setRows(null); setErr("");
        const load = { tb: () => fyTrialBalance(fyId), pl: () => profitLoss(fyId), bs: () => balanceSheet(fyId), gl: () => fyLedger(accountId, fyId) }[report];
        let live = true;   // ignore a reply that arrives after the user picked another report / account / year
        load().then(r => live && setRows(r)).catch(e => live && setErr(errText(e)));
        return () => { live = false; };
    }, [fyId, report, accountId, version]);

    if (!years.length) return <Notice tone="info">Create a fiscal year first (Setup → Fiscal years & calendar).</Notice>;
    const reportPicker = (
        <div style={{ minWidth: 170 }}><span style={text.label}>Report</span>
            <Select aria-label="Report" value={report} onChange={e => setReport(e.target.value)}>
                {REPORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select></div>);
    if (FLEX[report]) return <div><div style={{ marginBottom: 10 }}>{reportPicker}</div><PeriodStatements ctx={ctx} kind={FLEX[report]} /></div>;
    const provisional = !!status?.provisional;
    const exportProvisional = provisional || !!ctx.provisionalActivation;   // 021: on without the auditor's chart review
    const title = `${REPORTS.find(([k]) => k === report)[1]} FY ${fy?.label}${report === "gl" && account ? ` ${account.code} ${account.name}` : ""}`;
    // Exports carry the year's own status (never the opening-balances wording) and name every OPEN matter — never as a figure.
    const open = openItems.filter(o => o.status === "open");
    const yearNote = status && provisional ? `FY ${status.fiscal_year} ${status.note}${open.length ? `. OPEN, not determined (not zero): ${open.map(o => o.ref).join(", ")}; ${open.filter(o => o.blocks_final).length} block closing` : ""}` : undefined;
    const exportNote = [ctx.provisionalActivation && PROVISIONAL_ACTIVATION_NOTE, yearNote].filter(Boolean).join("; ") || undefined;

    let body = null, exportCols = [], exportRows = [];
    if (report === "tb") {
        const r = rows || [];
        const dr = r.reduce((a, x) => a + Math.max(Number(x.closing), 0), 0), cr = r.reduce((a, x) => a + Math.max(-Number(x.closing), 0), 0);
        exportCols = [{ header: "Code", exportValue: x => x.code }, { header: "Account", exportValue: x => x.name }, { header: "Opening", exportValue: x => x.opening },
            { header: "Debit", exportValue: x => x.debit }, { header: "Credit", exportValue: x => x.credit },
            { header: "Closing debit", exportValue: x => Math.max(Number(x.closing), 0) }, { header: "Closing credit", exportValue: x => Math.max(-Number(x.closing), 0) },
            { header: "Note", exportValue: x => x.note || "" }];
        exportRows = r;
        body = <>
            {rows && <div style={{ marginBottom: 10 }}><StatusBadge status={Math.abs(dr - cr) < 0.005 ? "confirmed" : "review"} label={Math.abs(dr - cr) < 0.005 ? "Debits equal credits" : "Does not agree"} /></div>}
            <Table cols={[["Account"], ["Opening", 1], ["Debit", 1], ["Credit", 1], ["Closing debit", 1], ["Closing credit", 1]]}>
                {!rows ? <SkeletonTableRows cols={6} cellStyle={cell} /> : r.length === 0 ? <tr><td style={cell} colSpan={6}>Nothing posted in this fiscal year.</td></tr> : <>
                    {r.map(x => <tr key={x.account_id}>
                        <td style={cell}><span style={mono}>{x.code}</span>{x.name}{x.note && <div style={text.caption}>{x.note}</div>}</td>
                        <td style={num}>{Number(x.opening) ? npr(x.opening) : ""}</td><td style={num}>{Number(x.debit) ? npr(x.debit) : ""}</td>
                        <td style={num}>{Number(x.credit) ? npr(x.credit) : ""}</td><td style={num}>{Number(x.closing) > 0 ? npr(x.closing) : ""}</td>
                        <td style={num}>{Number(x.closing) < 0 ? npr(-x.closing) : ""}</td></tr>)}
                    <tr><td style={{ ...cell, fontWeight: 700 }}>Total</td><td style={num} /><td style={num} /><td style={num} />
                        <td style={{ ...num, fontWeight: 700 }}>{npr(dr)}</td><td style={{ ...num, fontWeight: 700 }}>{npr(cr)}</td></tr></>}
            </Table></>;
    } else if (report === "pl" || report === "bs") {
        const r = rows || [];
        exportCols = [{ header: "Section", exportValue: x => x.section }, { header: "Code", exportValue: x => x.code || "" },
            { header: "Line", exportValue: x => x.name }, { header: "Amount", exportValue: x => x.amount }];
        exportRows = r;
        const sectionName = { income: "Income", expense: "Expenses", asset: "Assets", liability: "Liabilities", equity: "Equity" };
        let last = null;
        // Two columns fit a phone: no minimum width, so the figures are never hidden to the right.
        body = <Table cols={[["Line"], ["NPR", 1]]} minWidth={0}>
            {!rows ? <SkeletonTableRows cols={2} cellStyle={cell} /> : r.flatMap((x, i) => {
                const out = [];
                if (!TOTAL.test(x.section) && x.section !== last) {
                    last = x.section;
                    out.push(<tr key={`h${i}`}><td colSpan={2} style={{ ...cell, fontWeight: 700, background: T.surface2 }}>{sectionName[x.section] || x.section}</td></tr>);
                }
                const total = TOTAL.test(x.section);
                out.push(<tr key={i}>
                    <td style={{ ...cell, fontWeight: total ? 700 : 400, paddingLeft: total ? 12 : 24 }}>{x.code && <span style={mono}>{x.code}</span>}{x.name}</td>
                    <td style={{ ...num, fontWeight: total ? 700 : 400 }}>{npr(x.amount)}</td></tr>);
                return out;
            })}
        </Table>;
    } else {
        const r = rows || [];
        exportCols = [{ header: "Date (AD)", exportValue: x => x.line_date }, { header: "Voucher", exportValue: x => x.display_no || "" },
            { header: "Source", exportValue: x => x.source_ref || "" }, { header: "Description", exportValue: x => x.narration || "" },
            { header: "Party", exportValue: x => x.party_name || "" }, { header: "Debit", exportValue: x => x.debit ?? "" },
            { header: "Credit", exportValue: x => x.credit ?? "" }, { header: "Balance", exportValue: x => x.balance }];
        exportRows = r;
        body = <Table cols={[["Date"], ["Voucher"], ["Source"], ["Description"], ["Debit", 1], ["Credit", 1], ["Balance", 1]]}>
            {!rows ? <SkeletonTableRows cols={7} cellStyle={cell} /> : r.map((x, i) => (
                <tr key={i} onClick={x.entry_id ? () => openEntry(x.entry_id) : undefined} style={{ cursor: x.entry_id ? "pointer" : "default", background: x.is_opening ? T.surface2 : "transparent" }}>
                    <td style={cell}><DateChip iso={x.line_date} cal={cal} display={display} /></td>
                    <td style={{ ...cell, ...mono }}>{x.display_no || ""}</td>
                    <td style={{ ...cell, ...mono }}>{x.source_ref || ""}</td>
                    <td style={cell}>{x.is_opening ? <b>Opening balance</b> : x.narration}{x.party_name ? <span style={{ color: T.textMuted }}> · {x.party_name}</span> : null}</td>
                    <td style={num}>{Number(x.debit) ? npr(x.debit) : ""}</td><td style={num}>{Number(x.credit) ? npr(x.credit) : ""}</td>
                    <td style={{ ...num, fontWeight: 650, color: T.textStrong }}>{npr(x.balance)}</td></tr>))}
        </Table>;
    }

    return (
        <div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
                <div style={{ minWidth: 150 }}><span style={text.label}>Fiscal year</span>
                    <Select aria-label="Fiscal year" value={fyId} onChange={e => setFyId(e.target.value)}>
                        {years.map(y => <option key={y.id} value={y.id}>FY {y.label}</option>)}
                    </Select></div>
                <div style={{ minWidth: 170 }}><span style={text.label}>Report</span>
                    <Select aria-label="Report" value={report} onChange={e => setReport(e.target.value)}>
                        {REPORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </Select></div>
                {report === "gl" && (
                    <div style={{ minWidth: 260 }}><span style={text.label}>Account</span>
                        <Select aria-label="Account" value={accountId} onChange={e => setAccountId(e.target.value)}>
                            {Object.keys(TYPE_INFO).map(type => (
                                <optgroup key={type} label={TYPE_INFO[type][0]}>
                                    {postable.filter(a => a.type === type).map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                                </optgroup>))}
                        </Select></div>)}
                {status && <span role="note" style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "3px 10px", alignSelf: "center",
                    color: provisional ? T.warning : T.success, background: provisional ? T.warningBg : T.success + "1f" }}>{status.note}</span>}
                {rows && <div style={{ marginLeft: "auto" }}><ExportMenu filename={`${report}-FY${fy?.label.replace("/", "-")}`} title={title} columns={exportCols} rows={exportRows} provisional={exportProvisional} note={exportNote} /></div>}
            </div>
            {status && fy && <div style={{ ...text.caption, marginBottom: 10 }}>
                FY {status.fiscal_year}: {status.start_date} to {status.end_date}{status.historical_package ? ` · Historical package: ${status.historical_package}` : ""}
                {status.is_books_start_year ? " · Books start year" : ""}. Posted entries only; closing entries excluded.
            </div>}
            {err && <Notice tone="danger">{err}</Notice>}
            {openItems.some(o => o.status === "open") && (
                <div style={{ marginBottom: 14 }}>
                    <Notice tone="warning">
                        <b>OPEN — not determined, not zero.</b> These matters are not reflected in the figures below. {status?.blocking_open_items > 0 && `${status.blocking_open_items} of them block the final closing of FY ${fy?.label}.`}
                        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                            {openItems.filter(o => o.status === "open").map(o => <li key={o.id}><b>{o.ref}</b> {o.title} — OPEN{o.blocks_final ? " (blocks final closing)" : ""}</li>)}
                        </ul>
                    </Notice>
                </div>)}
            {body}
        </div>
    );
}
